/**
 * Centralized Token Service
 * Manages authentication token lifecycle across the entire application
 * All modules should use this service to access and manage tokens
 */

import API_ENDPOINTS from "../api/endpoints";

class TokenService {
  constructor() {
    this.TOKEN_KEY = "token";
    this.TOKEN_EXPIRY_KEY = "tokenExpiryTime";
    this.REFRESH_TOKEN_KEY = "refreshToken";
    this.REFRESH_TOKEN_EXPIRY_KEY = "refreshTokenExpiryTime";
    this.USER_NAME_KEY = "userName";
    this.isRefreshing = false;
    this.refreshSubscribers = [];
  }

  decodeTokenPayload(token) {
    if (!token) {
      return null;
    }

    try {
      const payload = token.split(".")[1];
      if (!payload) {
        return null;
      }

      const normalizedPayload = payload.replace(/-/g, "+").replace(/_/g, "/");
      const paddedPayload = normalizedPayload.padEnd(
        Math.ceil(normalizedPayload.length / 4) * 4,
        "=",
      );

      return JSON.parse(atob(paddedPayload));
    } catch (error) {
      console.error("[TokenService] Error decoding token payload:", error);
      return null;
    }
  }

  getUserNameFromToken(token = this.getToken()) {
    const payload = this.decodeTokenPayload(token);
    return payload?.user_name || payload?.userName || payload?.nameid || null;
  }

  /**
   * Save token and refresh token with their expiry times to localStorage
   * @param {string} token - The authentication token (JWT)
   * @param {string} expiryTime - ISO 8601 expiry time (e.g., "2026-04-29T03:23:44.6186669Z")
   * @param {string} refreshToken - The refresh token (long-lived)
   * @param {string} refreshTokenExpiryTime - ISO 8601 refresh token expiry time
   */
  setToken(token, expiryTime = null, refreshToken = null, refreshTokenExpiryTime = null) {
    if (!token) {
      console.warn("[TokenService] Attempting to set an empty token");
      return;
    }
    if (!expiryTime) {
      console.error("[TokenService] Token rejected: expiryTime is required. Tokens without an expiry cannot be validated.");
      return;
    }
    try {
      localStorage.setItem(this.TOKEN_KEY, token);
      const userName = this.getUserNameFromToken(token);

      if (userName) {
        localStorage.setItem(this.USER_NAME_KEY, userName);
      } else {
        localStorage.removeItem(this.USER_NAME_KEY);
      }

      localStorage.setItem(this.TOKEN_EXPIRY_KEY, expiryTime);

      // When refresh token fields are explicitly provided, write them.
      // When null/undefined (e.g. no refresh token in this login), clear any
      // stale values so a previous session's refresh token cannot leak into
      // the new session.
      if (refreshToken) {
        localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
      } else {
        localStorage.removeItem(this.REFRESH_TOKEN_KEY);
      }

      if (refreshTokenExpiryTime) {
        localStorage.setItem(this.REFRESH_TOKEN_EXPIRY_KEY, refreshTokenExpiryTime);
      } else {
        localStorage.removeItem(this.REFRESH_TOKEN_EXPIRY_KEY);
      }

      if (refreshToken || refreshTokenExpiryTime) {
        console.log("[TokenService] Token and refresh token metadata saved");
      } else {
        console.log(`[TokenService] Token saved with expiry: ${expiryTime}`);
      }
    } catch (error) {
      console.error("[TokenService] Error saving token:", error);
    }
  }

  /**
   * Retrieve token from localStorage
   * @returns {string|null} The stored token or null if not found
   */
  getToken() {
    try {
      return localStorage.getItem(this.TOKEN_KEY);
    } catch (error) {
      console.error("[TokenService] Error retrieving token:", error);
      return null;
    }
  }

  /**
   * Retrieve refresh token from localStorage
   * @returns {string|null} The stored refresh token or null if not found
   */
  getRefreshToken() {
    try {
      return localStorage.getItem(this.REFRESH_TOKEN_KEY);
    } catch (error) {
      console.error("[TokenService] Error retrieving refresh token:", error);
      return null;
    }
  }

  /**
   * Get refresh token expiry time
   * @returns {string|null} ISO 8601 expiry time or null
   */
  getRefreshTokenExpiryTime() {
    try {
      return localStorage.getItem(this.REFRESH_TOKEN_EXPIRY_KEY);
    } catch (error) {
      console.error("[TokenService] Error retrieving refresh token expiry time:", error);
      return null;
    }
  }

  /**
   * Get token expiry time
   * @returns {string|null} ISO 8601 expiry time or null
   */
  getTokenExpiryTime() {
    try {
      return localStorage.getItem(this.TOKEN_EXPIRY_KEY);
    } catch (error) {
      console.error(
        "[TokenService] Error retrieving token expiry time:",
        error,
      );
      return null;
    }
  }

  /**
   * Check if refresh token has expired
   * @returns {boolean} True if refresh token has expired, false otherwise
   */
  isRefreshTokenExpired() {
    try {
      const expiryTime = this.getRefreshTokenExpiryTime();
      if (!expiryTime) {
        console.warn("[TokenService] No refresh token expiry time found");
        return true;
      }

      const expiryDate = new Date(expiryTime);

      if (Number.isNaN(expiryDate.getTime())) {
        console.warn(`[TokenService] Refresh token expiry is not a valid date: "${expiryTime}" — treating as expired`);
        return true;
      }

      const now = new Date();
      const isExpired = now >= expiryDate;
      if (isExpired) {
        console.warn(`[TokenService] Refresh token expired at ${expiryTime}`);
      }
      return isExpired;
    } catch (error) {
      console.error("[TokenService] Error checking refresh token expiry:", error);
      return true;
    }
  }

  /**
   * Check if a valid refresh token exists and is not expired
   * @returns {boolean} True if refresh token exists and has not expired
   */
  hasValidRefreshToken() {
    return !!this.getRefreshToken() && !!this.getRefreshTokenExpiryTime() && !this.isRefreshTokenExpired();
  }

  /**
   * Check if token has expired based on server's expiry time
   * @returns {boolean} True if token has expired, false otherwise
   */
  isTokenExpired() {
    try {
      const expiryTime = this.getTokenExpiryTime();
      if (!expiryTime) {
        console.warn("[TokenService] No expiry time found — treating token as expired");
        return true;
      }

      const expiryDate = new Date(expiryTime);
      const now = new Date();

      const isExpired = now >= expiryDate;
      if (isExpired) {
        console.warn(`[TokenService] Token expired at ${expiryTime}`);
      }
      return isExpired;
    } catch (error) {
      console.error("[TokenService] Error checking token expiry:", error);
      return false;
    }
  }

  /**
   * Get time remaining until token expires (in milliseconds)
   * @returns {number|null} Milliseconds remaining or null if no expiry time
   */
  getTimeUntilExpiry() {
    try {
      const expiryTime = this.getTokenExpiryTime();
      if (!expiryTime) {
        return null;
      }

      const expiryDate = new Date(expiryTime);
      const now = new Date();
      const timeRemaining = expiryDate.getTime() - now.getTime();

      return timeRemaining > 0 ? timeRemaining : 0;
    } catch (error) {
      console.error("[TokenService] Error calculating time until expiry:", error);
      return null;
    }
  }

  /**
   * Check if a valid token exists and is not expired.
   * @returns {boolean} True if token exists, expiry is stored, and token has not expired
   */
  hasValidToken() {
    return !!this.getToken() && !!this.getTokenExpiryTime() && !this.isTokenExpired();
  }

  /**
   * Clear the token from localStorage
   */
  clearToken() {
    try {
      localStorage.removeItem(this.TOKEN_KEY);
      localStorage.removeItem(this.TOKEN_EXPIRY_KEY);
      localStorage.removeItem(this.USER_NAME_KEY);
      console.log("[TokenService] Token cleared successfully");
    } catch (error) {
      console.error("[TokenService] Error clearing token:", error);
    }
  }

  /**
   * Subscribe to token refresh events
   * Used by axios interceptor to queue requests while token is being refreshed
   * @param {function} callback - Function called with refreshed access token, or null on failure
   */
  onTokenRefreshed(callback) {
    this.refreshSubscribers.push(callback);
  }

  /**
   * Notify all subscribers that token refresh finished
   * @param {string|null} token - refreshed access token, or null when refresh failed
   */
  notifyTokenRefreshed(token) {
    this.refreshSubscribers.forEach((callback) => callback(token));
    this.refreshSubscribers = [];
  }

  /**
   * Set session start time
   * @param {number} timestamp - Session start time in milliseconds
   * @deprecated Backend tokenExpiryTime is now the source of truth
   */
  setSessionStartTime(timestamp) {
    // no-op: session lifetime is now managed by backend tokenExpiryTime
  }

  /**
   * Get session start time
   * @returns {number|null}
   * @deprecated Backend tokenExpiryTime is now the source of truth
   */
  getSessionStartTime() {
    return null;
  }

  getUserName() {
    try {
      const storedUserName = localStorage.getItem(this.USER_NAME_KEY);
      if (storedUserName) {
        return storedUserName;
      }

      const tokenUserName = this.getUserNameFromToken();
      if (tokenUserName) {
        localStorage.setItem(this.USER_NAME_KEY, tokenUserName);
      }

      return tokenUserName;
    } catch (error) {
      console.error("[TokenService] Error retrieving user name:", error);
      return null;
    }
  }

  /**
   * Get authorization header object for API requests
   * @returns {object} Headers object with Authorization header if valid token exists
   */
  getAuthHeader() {
    if (this.hasValidToken()) {
      return {
        Authorization: `Bearer ${this.getToken()}`,
      };
    }
    return {};
  }

  /**
   * Centralized token refresh using the refresh token.
   * Used for both proactive (silent) refresh and reactive (401) refresh.
   * @param {Object} options - Configuration options
   * @param {boolean} options.forceLogoutOnFailure - If true, clears session and dispatches auth:unauthorized on failure
   * @returns {Promise<{success: boolean, token?: string, error?: Error}>} Result object
   */
  async refreshAccessToken({ forceLogoutOnFailure = false } = {}) {
    // If already refreshing, wait for the current refresh to complete
    if (this.isRefreshing) {
      console.log("[TokenService] Refresh already in progress, waiting...");
      return new Promise((resolve) => {
        this.onTokenRefreshed((token) => {
          resolve(token ? { success: true, token } : { success: false });
        });
      });
    }

    // Check if we have a valid refresh token
    if (!this.hasValidRefreshToken()) {
      console.warn("[TokenService] No valid refresh token available");
      if (forceLogoutOnFailure) {
        this.clearAllSessionData();
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
      }
      return { success: false, error: new Error("No valid refresh token") };
    }

    this.isRefreshing = true;
    console.info("[TokenService] Starting token refresh...");

    try {
      // Import api dynamically to avoid circular dependency
      const { default: api } = await import("../api/axiosConfig");

      const refreshResponse = await api.post(
        API_ENDPOINTS.AUTH.REFRESH,
        { refreshToken: this.getRefreshToken() },
        { _skipRefresh: true } // Prevent recursive refresh attempts
      );

      const refreshData = refreshResponse.data?.data;
      if (refreshResponse.status === 200 && refreshData?.token && refreshData?.tokenExpiryTime) {
        const {
          token,
          tokenExpiryTime,
          refreshToken: rotatedRefreshToken,
          refreshTokenExpiryTime: rotatedRefreshTokenExpiryTime,
        } = refreshData;

        // Persist rotated refresh token when provided; otherwise keep current values
        const nextRefreshToken = rotatedRefreshToken || this.getRefreshToken();
        const nextRefreshTokenExpiryTime = rotatedRefreshTokenExpiryTime || this.getRefreshTokenExpiryTime();

        // Update tokens in storage
        this.setToken(token, tokenExpiryTime, nextRefreshToken, nextRefreshTokenExpiryTime);

        console.info("[TokenService] Token refresh successful");

        // Reset flag before notifying to keep state consistent for subscribers
        this.isRefreshing = false;
        this.notifyTokenRefreshed(token);

        return { success: true, token };
      } else {
        throw new Error("Refresh response missing token data");
      }
    } catch (error) {
      console.error("[TokenService] Token refresh failed:", error);
      this.isRefreshing = false;
      this.notifyTokenRefreshed(null);

      if (forceLogoutOnFailure) {
        this.clearAllSessionData();
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
      }

      return { success: false, error };
    }
  }

  /**
   * Clear all session data (token, refresh token, expiry times)
   */
  clearAllSessionData() {
    try {
      localStorage.removeItem(this.TOKEN_KEY);
      localStorage.removeItem(this.TOKEN_EXPIRY_KEY);
      localStorage.removeItem(this.REFRESH_TOKEN_KEY);
      localStorage.removeItem(this.REFRESH_TOKEN_EXPIRY_KEY);
      localStorage.removeItem(this.USER_NAME_KEY);
      // Also clear legacy sessionStartTime key if present from older sessions
      localStorage.removeItem('sessionStartTime');
      this.isRefreshing = false;
      this.refreshSubscribers = [];
      console.log("[TokenService] All session data cleared");
    } catch (error) {
      console.error("[TokenService] Error clearing session data:", error);
    }
  }
}

// Create singleton instance
const tokenService = new TokenService();

// Expose globally so Vue modules can access it
if (typeof window !== "undefined") {
  window.TokenService = tokenService;
}

export default tokenService;
